/**
 * Hash de mot de passe via PBKDF2 + Web Crypto API.
 * 600 000 itérations (recommandation OWASP 2024).
 * Sortie : hash base64 + salt base64 séparé.
 */
export class PasswordHasher {
  static readonly ITERATIONS = 600_000;
  static readonly KEY_LEN_BITS = 256;
  static readonly SALT_BYTES = 16;

  /** Génère un sel aléatoire base64. */
  static generateSalt(): string {
    const bytes = new Uint8Array(this.SALT_BYTES);
    crypto.getRandomValues(bytes);
    return this.toBase64(bytes);
  }

  /** Hash un mot de passe avec sel fourni. */
  static async hash(password: string, salt: string): Promise<string> {
    const saltBytes = this.fromBase64(salt);
    const passwordBytes = new TextEncoder().encode(password);
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      passwordBytes,
      { name: 'PBKDF2' },
      false,
      ['deriveBits'],
    );
    const derived = await crypto.subtle.deriveBits(
      { name: 'PBKDF2', salt: saltBytes, iterations: this.ITERATIONS, hash: 'SHA-256' },
      keyMaterial,
      this.KEY_LEN_BITS,
    );
    return this.toBase64(new Uint8Array(derived));
  }

  /** Vérifie un mot de passe contre un hash existant. */
  static async verify(password: string, hash: string, salt: string): Promise<boolean> {
    const computed = await this.hash(password, salt);
    return this.constantTimeEquals(computed, hash);
  }

  /** Comparaison à temps constant pour éviter les attaques timing. */
  private static constantTimeEquals(a: string, b: string): boolean {
    if (a.length !== b.length) return false;
    let result = 0;
    for (let i = 0; i < a.length; i++) result |= a.charCodeAt(i) ^ b.charCodeAt(i);
    return result === 0;
  }

  private static toBase64(bytes: Uint8Array): string {
    let s = '';
    for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
    return btoa(s);
  }

  private static fromBase64(b64: string): Uint8Array<ArrayBuffer> {
    const s = atob(b64);
    const bytes = new Uint8Array(new ArrayBuffer(s.length));
    for (let i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i);
    return bytes;
  }
}
